import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';

/* ── Shared Tailwind class strings (matches ItemMasterDetail theme) ── */
const inp = 'w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue';
const sel = inp + " appearance-none bg-no-repeat bg-[length:10px_10px] bg-[position:right_6px_center] pr-5"
  + " bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20d%3D%22M0%200l5%206%205-6z%22%20fill%3D%22%23555%22%2F%3E%3C%2Fsvg%3E')]";
const chk = 'accent-sap-blue w-3.5 h-3.5';

const COUNTRY_OPTS = [
  '', 'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Bahrain', 'Bangladesh', 'Belgium', 'Bhutan', 'Brazil', 'Cambodia', 'Canada',
  'Chile', 'China', 'Colombia', 'Czech Republic', 'Denmark', 'Egypt', 'Ethiopia',
  'Finland', 'France', 'Germany', 'Ghana', 'Greece', 'Hong Kong', 'Hungary',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan',
  'Jordan', 'Kenya', 'Kuwait', 'Malaysia', 'Mexico', 'Myanmar', 'Nepal',
  'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Oman', 'Pakistan',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Saudi Arabia', 'Singapore', 'South Africa', 'South Korea', 'Spain',
  'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand', 'Turkey',
  'UAE', 'United Kingdom', 'United States', 'Vietnam',
];

const STATES_BY_COUNTRY = {
  'India': [
    '', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu & Kashmir',
    'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
    'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  ],
  'United States': [
    '', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
    'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
    'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana',
    'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
    'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
  ],
  'United Kingdom': ['', 'England', 'Scotland', 'Wales', 'Northern Ireland'],
  'Canada': [
    '', 'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan',
  ],
  'Australia': [
    '', 'New South Wales', 'Victoria', 'Queensland', 'South Australia',
    'Western Australia', 'Tasmania', 'Northern Territory', 'Australian Capital Territory',
  ],
  'Germany': [
    '', 'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia',
    'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt',
    'Schleswig-Holstein', 'Thuringia',
  ],
  'UAE': ['', 'Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'],
};

/* (locations loaded from DB) */

/* ── Accounting field definitions (matches SAP B1 Warehouse Accounting tab) ── */
const ACCT_FIELDS = [
  { key: 'expense_account', label: 'Expense Account' },
  { key: 'revenue_account', label: 'Revenue Account' },
  { key: 'inventory_account', label: 'Inventory Account' },
  { key: 'cost_of_goods_sold_account', label: 'Cost of Goods Sold Account' },
  { key: 'allocation_account', label: 'Allocation Account' },
  { key: 'variance_account', label: 'Variance Account' },
  { key: 'price_difference_account', label: 'Price Difference Account' },
  { key: 'negative_inventory_adj_account', label: 'Negative Inventory Adjustment' },
  { key: 'inventory_offset_decrease_account', label: 'Inventory Offset - Decrease' },
  { key: 'inventory_offset_increase_account', label: 'Inventory Offset - Increase' },
  { key: 'sales_returns_account', label: 'Sales Returns Account' },
  { key: 'revenue_account_eu', label: 'Revenue Account - EU' },
  { key: 'expense_account_eu', label: 'Expense Account - EU' },
  { key: 'revenue_account_foreign', label: 'Revenue Account - Foreign' },
  { key: 'expense_account_foreign', label: 'Expense Account - Foreign' },
  { key: 'exchange_rate_diff_account', label: 'Exchange Rate Differences' },
  { key: 'goods_clearing_account', label: 'Goods Clearing Account' },
  { key: 'gl_decrease_account', label: 'G/L Decrease Account' },
  { key: 'gl_increase_account', label: 'G/L Increase Account' },
  { key: 'wip_inventory_account', label: 'WIP Inventory Account' },
  { key: 'wip_inventory_variance_account', label: 'WIP Inventory Variance' },
  { key: 'wip_offset_pl_account', label: 'WIP Offset P&L Account' },
  { key: 'inventory_offset_pl_account', label: 'Inventory Offset P&L Account' },
  { key: 'expense_clearing_account', label: 'Expense Clearing Account' },
  { key: 'stock_in_transit_account', label: 'Stock In Transit Account' },
  { key: 'shipped_goods_account', label: 'Shipped Goods Account' },
  { key: 'sales_credit_account', label: 'Sales Credit Account' },
  { key: 'purchase_credit_account', label: 'Purchase Credit Account' },
  { key: 'sales_credit_account_foreign', label: 'Sales Credit Account - Foreign' },
  { key: 'purchase_credit_account_foreign', label: 'Purchase Credit Account - Foreign' },
  { key: 'sales_credit_account_eu', label: 'Sales Credit Account - EU' },
  { key: 'purchase_credit_account_eu', label: 'Purchase Credit Account - EU' },
];

const EMPTY_ACCOUNTS = Object.fromEntries(ACCT_FIELDS.map(f => [f.key, '']));

const EMPTY_FORM = {
  wh_code: '', wh_name: '', location: '', inactive: false, drop_ship: false,
  nettable: true, consignment_bp: '', street: '', street_no: '', block: '',
  building_floor_room: '', zip_code: '', city: '', county: '', country_region: '',
  state: '', federal_tax_id: '', gln: '', tax_office: '',
  address_name_2: '', address_name_3: '', enable_bin_locations: false,
  accounts: { ...EMPTY_ACCOUNTS },
};

/* ── Row helper (label + field) matching ItemMasterDetail ── */
function Row({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] font-medium text-sap-text whitespace-nowrap w-44 text-right shrink-0">{label}</label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default function WarehouseSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const openCode = location.state?.openCode || null;

  /* State */
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [tab, setTab]               = useState('general');
  const [saving, setSaving]         = useState(false);
  const [message, setMessage]       = useState('');
  /* Find popup state */
  const [findOpen, setFindOpen] = useState(false);
  const [findQ, setFindQ]       = useState('');
  const findRef = useRef(null);
  /* Account lookup popup state */
  const [lookupOpen, setLookupOpen]   = useState(null);  // key of the field being looked up
  const [lookupQ, setLookupQ]         = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [acctNames, setAcctNames]     = useState({});    // cache: code → name
  const [coaCategories, setCoaCategories] = useState([]); // top-level account categories
  const [coaFilter, setCoaFilter]     = useState('');     // filter by category prefix
  const [coaSelected, setCoaSelected] = useState(null);   // highlighted account in CoA dialog
  const [coaDetail, setCoaDetail]     = useState(null);   // detail of selected account
  const lookupRef = useRef(null);

  /* Locations state (fetched from DB) */
  const [locations, setLocations] = useState([]);
  const [locSetupOpen, setLocSetupOpen] = useState(false);
  const [locRows, setLocRows] = useState([]); // editing rows in popup
  const locRef = useRef(null);

  /* Fetch warehouses */
  const fetchWarehouses = useCallback(async () => {
    try {
      const data = await api.get('/warehouses');
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setWarehouses(rows);
    } catch { setWarehouses([]); }
    finally { setLoading(false); }
  }, []);

  /* Fetch locations */
  const fetchLocations = useCallback(async () => {
    try {
      const data = await api.get('/warehouses/locations');
      setLocations(Array.isArray(data) ? data : []);
    } catch { setLocations([]); }
  }, []);

  /* Fetch top-level account categories */
  const fetchCategories = useCallback(async () => {
    try {
      const data = await api.get('/warehouses/accounts/categories');
      setCoaCategories(Array.isArray(data) ? data : []);
    } catch { setCoaCategories([]); }
  }, []);

  useEffect(() => { fetchWarehouses(); fetchLocations(); fetchCategories(); }, [fetchWarehouses, fetchLocations, fetchCategories]);

  /* Resolve account codes → names via lookup API — defined before selectWarehouse */
  const resolveAcctNames = useCallback(async (codes) => {
    const missing = codes.filter(c => !!c);
    for (const code of missing) {
      try {
        const resp = await api.get(`/warehouses/accounts/lookup?q=${encodeURIComponent(code)}`);
        const rows = Array.isArray(resp) ? resp : [];
        const match = rows.find(r => r.acct_code === code);
        if (match) setAcctNames(prev => ({ ...prev, [code]: match.acct_name }));
      } catch { /* ignore */ }
    }
  }, []);

  /* Select warehouse from list */
  const selectWarehouse = useCallback(async (wh) => {
    setSelected(wh.warehouse_id);
    // Fetch detail with accounts
    try {
      const d = await api.get(`/warehouses/${wh.warehouse_id}`);
      const accts = d.accounts || {};
      setForm({
        wh_code:              d.wh_code || '',
        wh_name:              d.wh_name || '',
        location:             d.location || '',
        inactive:             !!d.inactive,
        drop_ship:            !!d.drop_ship,
        nettable:             d.nettable !== 0,
        consignment_bp:       d.consignment_bp || '',
        street:               d.street || '',
        street_no:            d.street_no || '',
        block:                d.block || '',
        building_floor_room:  d.building_floor_room || '',
        zip_code:             d.zip_code || '',
        city:                 d.city || '',
        county:               d.county || '',
        country_region:       d.country_region || '',
        state:                d.state || '',
        federal_tax_id:       d.federal_tax_id || '',
        gln:                  d.gln || '',
        tax_office:           d.tax_office || '',
        address_name_2:       d.address_name_2 || '',
        address_name_3:       d.address_name_3 || '',
        enable_bin_locations: !!d.enable_bin_locations,
        accounts: Object.fromEntries(ACCT_FIELDS.map(f => [f.key, accts[f.key] || ''])),
      });
      // Resolve account names
      const codes = ACCT_FIELDS.map(f => accts[f.key]).filter(Boolean);
      if (codes.length) resolveAcctNames(codes);
    } catch {
      // Fallback to list data
      setForm({
        wh_code: wh.wh_code || '', wh_name: wh.wh_name || '', location: wh.location || '',
        inactive: !!wh.inactive, drop_ship: !!wh.drop_ship, nettable: wh.nettable !== 0,
        consignment_bp: wh.consignment_bp || '', street: wh.street || '',
        street_no: wh.street_no || '', block: wh.block || '',
        building_floor_room: wh.building_floor_room || '', zip_code: wh.zip_code || '',
        city: wh.city || '', county: wh.county || '', country_region: wh.country_region || '',
        state: wh.state || '', federal_tax_id: wh.federal_tax_id || '',
        gln: wh.gln || '', tax_office: wh.tax_office || '',
        address_name_2: wh.address_name_2 || '', address_name_3: wh.address_name_3 || '',
        enable_bin_locations: !!wh.enable_bin_locations,
        accounts: { ...EMPTY_ACCOUNTS },
      });
    }
    setTab('general');
    setMessage('');
  }, [resolveAcctNames]);

  /* Auto-select warehouse when navigated from Item Master with openCode */
  useEffect(() => {
    if (!openCode || warehouses.length === 0) return;
    const match = warehouses.find(w => w.wh_code === openCode);
    if (match) selectWarehouse(match);
  }, [openCode, warehouses, selectWarehouse]);

  /* Account lookup — fetch accounts for CoA dialog */
  const doLookup = async (q, catPrefix) => {
    setLookupQ(q);
    setLookupLoading(true);
    try {
      let url = `/warehouses/accounts/all?q=${encodeURIComponent(q || '')}`;
      const resp = await api.get(url);
      let rows = Array.isArray(resp) ? resp : [];
      // client-side filter by category prefix
      if (catPrefix) rows = rows.filter(r => r.acct_code.startsWith(catPrefix.charAt(0)));
      setLookupResults(rows);
    } catch { setLookupResults([]); }
    finally { setLookupLoading(false); }
  };

  /* Open Chart of Accounts dialog for a field */
  const openLookup = (fieldKey) => {
    setLookupOpen(fieldKey);
    setLookupQ('');
    setCoaFilter('');
    setCoaSelected(null);
    setCoaDetail(null);
    setLookupResults([]);
    doLookup('', '');
  };

  /* Select account in CoA list (highlight) */
  const highlightAccount = async (acct) => {
    setCoaSelected(acct.acct_code);
    try {
      const detail = await api.get(`/warehouses/accounts/detail/${encodeURIComponent(acct.acct_code)}`);
      setCoaDetail(detail);
    } catch { setCoaDetail({ acct_code: acct.acct_code, acct_name: acct.acct_name }); }
  };

  /* Confirm account selection (OK button or double-click) */
  const confirmAccount = (acct) => {
    if (!lookupOpen || !acct) return;
    setForm(f => ({
      ...f,
      accounts: { ...f.accounts, [lookupOpen]: acct.acct_code }
    }));
    setAcctNames(prev => ({ ...prev, [acct.acct_code]: acct.acct_name }));
    setLookupOpen(null);
    setCoaDetail(null);
    setCoaSelected(null);
  };

  /* Close lookup on Escape */
  useEffect(() => {
    if (!lookupOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setLookupOpen(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lookupOpen]);

  /* Close find popup on click outside */
  useEffect(() => {
    if (!findOpen) return;
    const handler = (e) => {
      if (findRef.current && !findRef.current.contains(e.target)) setFindOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [findOpen]);

  /* Close locations-setup popup on click outside */
  useEffect(() => {
    if (!locSetupOpen) return;
    const handler = (e) => {
      if (locRef.current && !locRef.current.contains(e.target)) setLocSetupOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [locSetupOpen]);

  /* Open Locations-Setup dialog */
  const openLocSetup = () => {
    setLocRows([
      ...locations.map(l => ({ ...l, _dirty: false })),
      { location_id: null, name: '', _dirty: false },
    ]);
    setLocSetupOpen(true);
  };

  /* Save locations from popup */
  const saveLocations = async () => {
    try {
      for (const row of locRows) {
        const name = row.name.trim();
        if (!name) continue;
        if (row.location_id) {
          // existing — update if changed
          const orig = locations.find(l => l.location_id === row.location_id);
          if (orig && orig.name !== name) {
            await api.put(`/warehouses/locations/${row.location_id}`, { name });
          }
        } else {
          // new row
          await api.post('/warehouses/locations', { name });
        }
      }
      // delete any rows removed by user
      for (const loc of locations) {
        if (!locRows.find(r => r.location_id === loc.location_id)) {
          await api.del(`/warehouses/locations/${loc.location_id}`);
        }
      }
      await fetchLocations();
      setLocSetupOpen(false);
    } catch (err) {
      setMessage(err.message || 'Failed to save locations.');
    }
  };

  const findFiltered = warehouses.filter(wh =>
    !findQ ||
    wh.wh_code?.toLowerCase().includes(findQ.toLowerCase()) ||
    wh.wh_name?.toLowerCase().includes(findQ.toLowerCase())
  );

  /* New warehouse */
  const handleNew = () => {
    setSelected(null);
    setForm({ ...EMPTY_FORM });
    setTab('general');
    setMessage('');
  };

  /* Save (add or update) */
  const handleSave = async () => {
    if (!form.wh_code.trim() || !form.wh_name.trim()) {
      setMessage('Warehouse Code and Name are required.');
      return;
    }
    setSaving(true); setMessage('');
    try {
      if (selected) {
        await api.put(`/warehouses/${selected}`, form);
        setMessage('Warehouse updated successfully.');
      } else {
        const data = await api.post('/warehouses', form);
        const newWh = data;
        setSelected(newWh.warehouse_id);
        setMessage('Warehouse added successfully.');
      }
      fetchWarehouses();
    } catch (err) {
      setMessage(err.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));



  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Warehouses - Setup</h1>
        <div className="flex items-center gap-2">
          <div className="relative" ref={findRef}>
            <button type="button" onClick={() => { setFindOpen(o => !o); setFindQ(''); }} className="btn-secondary text-xs py-1.5 px-3">
              Find
            </button>
            {findOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-sap-border rounded-lg shadow-xl"
                style={{ width: 320, maxHeight: 350 }}>
                <div className="px-3 py-2 border-b border-[#D5D0C8] bg-[#E8E4DC] rounded-t-lg">
                  <input value={findQ} onChange={e => setFindQ(e.target.value)}
                    placeholder="Search code or name…" autoFocus
                    className="w-full h-6 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-1 focus:ring-sap-blue/40" />
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                  {findFiltered.length === 0 ? (
                    <div className="py-4 text-center text-xs text-sap-textLight italic">No warehouses found</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-sap-text border-b border-sap-border sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">Code</th>
                          <th className="px-2 py-1.5 text-left font-medium">Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {findFiltered.map((wh, i) => (
                          <tr key={wh.warehouse_id}
                            onClick={() => { selectWarehouse(wh); setFindOpen(false); }}
                            className={`cursor-pointer transition-colors
                              ${selected === wh.warehouse_id
                                ? 'bg-sap-blueLight font-semibold'
                                : i % 2 === 0 ? 'bg-white hover:bg-sap-blueLight' : 'bg-[#F0F4F3] hover:bg-sap-blueLight'}`}>
                            <td className="px-2 py-1.5 text-sap-blue font-semibold">{wh.wh_code}</td>
                            <td className="px-2 py-1.5 text-sap-text">{wh.wh_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
          <button type="button" onClick={handleNew} className="btn-primary">
            + New
          </button>
        </div>
      </div>

      {/* ═══ Success / Error message ═══ */}
      {message && (
        <div className={`rounded-lg px-4 py-2.5 flex items-center gap-2 shadow-sm text-xs font-semibold
          ${message.includes('success')
            ? 'bg-green-50 border border-green-300 text-green-800'
            : 'bg-red-50 border border-red-300 text-red-800'}`}>
          {message}
          <button onClick={() => setMessage('')} className="ml-auto text-sm font-bold px-1 opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      {/* ═══ Main form (full width, no left panel) ═══ */}
      <div className="flex flex-col bg-white rounded-2xl shadow-sm border border-sap-border/60 overflow-hidden"
        style={{ minHeight: 'calc(100vh - 160px)' }}>
          {/* SAP Title bar */}
          <div className="bg-[#E8E4DC] border-b border-[#C4BFB6] px-4 py-1.5 flex items-center gap-3">
            <span className="text-xs font-semibold text-[#333]">Warehouse Setup</span>
            {selected && <span className="text-[10px] text-[#666]">({form.wh_code})</span>}
            {!selected && <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">New</span>}
          </div>

          {/* Persistent Header: Warehouse Code + Name */}
          <div className="bg-[#F4F3EF] border-b border-[#D5D0C8] px-4 py-3">
            <div className="grid grid-cols-[1fr_1fr] gap-x-12 gap-y-1.5">
              <Row label="Warehouse Code">
                <input value={form.wh_code} onChange={e => s('wh_code', e.target.value)}
                  disabled={!!selected}
                  className={`${inp} ${selected ? 'bg-gray-100' : ''}`} style={{ maxWidth: 200 }} />
              </Row>
              <Row label="Warehouse Name">
                <input value={form.wh_name} onChange={e => s('wh_name', e.target.value)}
                  className={inp} />
              </Row>
            </div>
          </div>

          {/* Tab bar (matches ItemMasterDetail style) */}
          <div className="bg-white border-b border-[#D5D0C8]">
            <div className="flex border-b border-[#C4BFB6] bg-[#E8E4DC] px-1">
              {['general', 'accounting'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 text-[11px] whitespace-nowrap border-t border-l border-r transition-colors focus:outline-none
                    ${tab === t
                      ? 'bg-white text-[#333] font-semibold border-[#C4BFB6] -mb-px z-10'
                      : 'text-[#666] bg-[#D5D0C8] border-transparent hover:bg-[#DDD9D1]'}`}>
                  {t === 'general' ? 'General' : 'Accounting'}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-4 min-h-[300px]">
              {tab === 'general' && (
                <div className="grid gap-y-[6px]" style={{ gridTemplateColumns: '170px 230px 60px 200px' }}>
                  {/* Row: Inactive | Drop-Ship */}
                  <div />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.inactive}
                      onChange={e => s('inactive', e.target.checked)} className={chk} />
                    <span className="text-[11px] text-[#333]">Inactive</span>
                  </label>
                  <div />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.drop_ship}
                      onChange={e => s('drop_ship', e.target.checked)} className={chk} />
                    <span className="text-[11px] text-[#333]">Drop-Ship</span>
                  </label>

                  {/* Spacer row */}
                  <div className="col-span-4 h-3" />

                  {/* Row: Location | Nettable */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Location</label>
                  <select value={form.location}
                    onChange={e => {
                      if (e.target.value === '__DEFINE_NEW__') { openLocSetup(); return; }
                      s('location', e.target.value);
                    }}
                    className={sel}>
                    <option value="">—</option>
                    {locations.map(l => <option key={l.location_id} value={l.name}>{l.name}</option>)}
                    <option value="__DEFINE_NEW__">Define New</option>
                  </select>
                  <div />
                  <label className="flex items-center gap-2 cursor-pointer self-center">
                    <input type="checkbox" checked={form.nettable}
                      onChange={e => s('nettable', e.target.checked)} className={chk} />
                    <span className="text-[11px] text-[#333] font-medium">Nettable</span>
                  </label>

                  {/* Spacer row */}
                  <div className="col-span-4 h-3" />

                  {/* Row: Consignment Business Partner */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Consignment Business Partner</label>
                  <input value={form.consignment_bp} onChange={e => s('consignment_bp', e.target.value)}
                    className={inp} />
                  <div /><div />

                  {/* Separator */}
                  <div className="col-span-4 h-4" />

                  {/* Row: Street/PO Box */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Street/PO Box</label>
                  <input value={form.street} onChange={e => s('street', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Street No. */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Street No.</label>
                  <input value={form.street_no} onChange={e => s('street_no', e.target.value)} className={inp} />
                  <div />
                  <label className="flex items-center gap-2 cursor-pointer self-center">
                    <input type="checkbox" checked={form.enable_bin_locations}
                      onChange={e => s('enable_bin_locations', e.target.checked)} className={chk} />
                    <span className="text-[11px] text-[#333]">Enable Bin Locations</span>
                  </label>

                  {/* Row: Block */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Block</label>
                  <input value={form.block} onChange={e => s('block', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Building/Floor/Room */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Building/Floor/Room</label>
                  <input value={form.building_floor_room} onChange={e => s('building_floor_room', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Zip Code */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Zip Code</label>
                  <input value={form.zip_code} onChange={e => s('zip_code', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: City */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">City</label>
                  <input value={form.city} onChange={e => s('city', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: County */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">County</label>
                  <input value={form.county} onChange={e => s('county', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Country/Region */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Country/Region</label>
                  <select value={form.country_region} onChange={e => { s('country_region', e.target.value); s('state', ''); }} className={sel}>
                    {COUNTRY_OPTS.map(o => <option key={o} value={o}>{o || '—'}</option>)}
                  </select>
                  <div /><div />

                  {/* Row: State (depends on Country) */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">State</label>
                  {STATES_BY_COUNTRY[form.country_region] ? (
                    <select value={form.state} onChange={e => s('state', e.target.value)} className={sel}>
                      {STATES_BY_COUNTRY[form.country_region].map(o => <option key={o} value={o}>{o || '—'}</option>)}
                    </select>
                  ) : (
                    <input value={form.state} onChange={e => s('state', e.target.value)} className={inp} />
                  )}
                  <div /><div />

                  {/* Row: Federal Tax ID */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Federal Tax ID</label>
                  <input value={form.federal_tax_id} onChange={e => s('federal_tax_id', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: GLN */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">GLN</label>
                  <input value={form.gln} onChange={e => s('gln', e.target.value)} className={inp} />
                  <div />
                  <button onClick={() => {
                    const q = encodeURIComponent([form.street, form.city, form.state, form.country_region].filter(Boolean).join(', '));
                    if (q) window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
                  }}
                    className="text-[11px] text-sap-blue underline hover:text-sap-blueDark transition-colors self-center text-left">
                    Show Location in Web Browser
                  </button>

                  {/* Row: Tax Office */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Tax Office</label>
                  <input value={form.tax_office} onChange={e => s('tax_office', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Address Name 2 */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Address Name 2</label>
                  <input value={form.address_name_2} onChange={e => s('address_name_2', e.target.value)} className={inp} />
                  <div /><div />

                  {/* Row: Address Name 3 */}
                  <label className="text-[11px] font-semibold text-sap-text self-center text-right pr-2">Address Name 3</label>
                  <input value={form.address_name_3} onChange={e => s('address_name_3', e.target.value)} className={inp} />
                  <div /><div />
                </div>
              )}

              {tab === 'accounting' && (
                <div>
                  <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-gray-50 text-sap-text border-b border-sap-border sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium w-56">#</th>
                          <th className="px-2 py-1.5 text-left font-medium" style={{ width: 160 }}>Account Code</th>
                          <th className="px-2 py-1.5 text-left font-medium">Account Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ACCT_FIELDS.map((field, i) => {
                          const code = form.accounts[field.key] || '';
                          const name = code ? (acctNames[code] || '') : '';
                          return (
                            <tr key={field.key}
                              className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'}>
                              <td className="px-2 py-1 text-[11px] text-sap-text font-medium border-b border-[#E8E4DC] truncate">
                                {field.label}
                              </td>
                              <td className="px-1 py-1 border-b border-[#E8E4DC]">
                                <div className="flex items-center gap-0.5">
                                  <button
                                    onClick={() => openLookup(field.key)}
                                    className="flex items-center justify-center w-5 h-5 rounded hover:bg-amber-100 transition-colors shrink-0"
                                    title="Choose account">
                                    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5">
                                      <path d="M4 2 L12 8 L4 14Z" fill="#D4A017" stroke="#B8860B" strokeWidth="0.5"/>
                                    </svg>
                                  </button>
                                  <input
                                    value={code}
                                    onChange={e => setForm(f => ({
                                      ...f, accounts: { ...f.accounts, [field.key]: e.target.value }
                                    }))}
                                    className="flex-1 h-[24px] px-1.5 text-xs border border-sap-border rounded bg-white
                                               focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue"
                                    style={{ minWidth: 80 }}
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-1 text-[11px] text-sap-textLight border-b border-[#E8E4DC] truncate">
                                {name}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer: Add / Update + Cancel */}
          <div className="px-4 py-2.5 border-t border-[#C4BFB6] bg-[#E8E4DC] flex items-center gap-3 mt-auto">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded
                         hover:bg-sap-blueDark disabled:opacity-50 transition-colors">
              {selected ? 'Update' : 'Add'}
            </button>
            <button onClick={handleNew}
              className="px-4 py-1.5 text-xs font-medium bg-white text-[#333] rounded border border-sap-border
                         hover:bg-[#f5f5f5] transition-colors">
              Cancel
            </button>
          </div>
        </div>

      {/* ═══ Chart of Accounts Dialog (SAP B1 style) ═══ */}
      {lookupOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div ref={lookupRef} className="bg-[#ECE9E1] border border-[#A09C94] rounded-lg shadow-2xl flex flex-col"
            style={{ width: 920, height: 580, maxWidth: '95vw', maxHeight: '90vh' }}>
            {/* Title bar */}
            <div className="px-4 py-2 bg-gray-50 border-b border-sap-border rounded-t-lg flex items-center justify-between shrink-0">
              <span className="text-sm font-semibold text-white">Chart of Accounts</span>
              <button onClick={() => setLookupOpen(null)} className="text-sap-textLight hover:text-sap-text text-lg font-bold px-1">×</button>
            </div>

            {/* Body: 3-column layout */}
            <div className="flex flex-1 overflow-hidden">

              {/* ── Left panel: G/L Account Details ── */}
              <div className="w-[260px] shrink-0 border-r border-[#C4BFB6] bg-[#F4F3EF] p-3 overflow-y-auto">
                <h3 className="text-[11px] font-bold text-sap-blue mb-2 border-b border-[#C4BFB6] pb-1">G/L Account Details</h3>
                {coaDetail ? (
                  <div className="space-y-2 text-[11px]">
                    <div>
                      <span className="font-semibold text-[#555]">G/L Account</span>
                      <div className="mt-0.5 px-2 py-1 bg-white border border-sap-border rounded text-xs font-medium">{coaDetail.acct_code}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-[#555]">Name</span>
                      <div className="mt-0.5 px-2 py-1 bg-white border border-sap-border rounded text-xs">{coaDetail.acct_name}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-[#555]">External Code</span>
                      <div className="mt-0.5 px-2 py-1 bg-white border border-sap-border rounded text-xs text-[#999]">{coaDetail.external_code || '—'}</div>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <span className="font-semibold text-[#555]">Account Type</span>
                      <span className="text-xs">{
                        coaDetail.acct_type === 'at_Expenses' ? 'Expenditure' :
                        coaDetail.acct_type === 'at_Revenues' ? 'Revenue' : 'Other'
                      }</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-[#555]">Active</span>
                      <span className="text-xs">{coaDetail.active_account === 'tYES' ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-[#555]">Balance</span>
                      <div className="mt-0.5 flex items-center gap-1">
                        <svg viewBox="0 0 16 16" className="w-3 h-3 shrink-0">
                          <path d="M4 2 L12 8 L4 14Z" fill="#D4A017" stroke="#B8860B" strokeWidth="0.5"/>
                        </svg>
                        <span className="px-2 py-1 bg-white border border-sap-border rounded text-xs font-medium">
                          {Number(coaDetail.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    {coaDetail.father_account_key && (
                      <div>
                        <span className="font-semibold text-[#555]">Parent Account</span>
                        <div className="mt-0.5 px-2 py-1 bg-white border border-sap-border rounded text-xs">{coaDetail.father_account_key}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[11px] text-[#999] italic pt-4">Select an account to view details</div>
                )}
              </div>

              {/* ── Center: Account list ── */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Search bar */}
                <div className="px-3 py-2 bg-[#E8E4DC] border-b border-[#C4BFB6] flex items-center gap-2 shrink-0">
                  <input
                    value={lookupQ}
                    onChange={e => { setLookupQ(e.target.value); doLookup(e.target.value, coaFilter); }}
                    placeholder="Search account code or name…"
                    className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                               focus:outline-none focus:ring-1 focus:ring-sap-blue/40"
                    autoFocus
                  />
                  <span className="text-[10px] text-[#888]">{lookupResults.length} accounts</span>
                </div>
                {/* Scrollable list */}
                <div className="flex-1 overflow-y-auto bg-white">
                  {lookupLoading ? (
                    <div className="py-8 text-center text-xs text-sap-textLight">Loading accounts…</div>
                  ) : lookupResults.length === 0 ? (
                    <div className="py-8 text-center text-xs text-sap-textLight italic">No accounts found</div>
                  ) : (
                    <div className="divide-y divide-[#E8E4DC]">
                      {lookupResults.map((r) => (
                        <div key={r.acct_code}
                          onClick={() => highlightAccount(r)}
                          onDoubleClick={() => confirmAccount(r)}
                          className={`px-3 py-1.5 cursor-pointer flex items-center gap-2 text-xs transition-colors
                            ${coaSelected === r.acct_code
                              ? 'bg-sap-blue text-white'
                              : 'hover:bg-[#E8F4F0]'}`}>
                          <span className={`font-medium shrink-0 w-32 ${coaSelected === r.acct_code ? 'text-amber-300' : 'text-sap-blue'}`}>
                            {r.acct_code}
                          </span>
                          <span className="truncate">{r.acct_name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Right panel: Category filter tabs ── */}
              <div className="w-[120px] shrink-0 border-l border-[#C4BFB6] bg-[#ECE9E1] overflow-y-auto p-1">
                <button
                  onClick={() => { setCoaFilter(''); doLookup(lookupQ, ''); }}
                  className={`w-full text-left px-2 py-1.5 text-[10px] font-semibold rounded mb-0.5 transition-colors
                    ${!coaFilter ? 'bg-sap-blue text-white' : 'text-[#333] hover:bg-[#D5D0C8]'}`}>
                  All
                </button>
                {coaCategories.map(cat => {
                  const prefix = cat.acct_code.charAt(0);
                  const active = coaFilter === cat.acct_code;
                  return (
                    <button key={cat.acct_code}
                      onClick={() => { setCoaFilter(cat.acct_code); doLookup(lookupQ, cat.acct_code); }}
                      className={`w-full text-left px-2 py-1.5 text-[10px] font-medium rounded mb-0.5 transition-colors truncate
                        ${active ? 'bg-sap-blue text-white' : 'text-[#333] hover:bg-[#D5D0C8]'}`}
                      title={cat.acct_name}>
                      {cat.acct_name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-[#C4BFB6] bg-[#E8E4DC] rounded-b-lg flex items-center gap-3 shrink-0">
              <button
                onClick={() => {
                  const acct = lookupResults.find(r => r.acct_code === coaSelected);
                  if (acct) confirmAccount(acct);
                }}
                disabled={!coaSelected}
                className="px-5 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded
                           hover:bg-sap-blueDark disabled:opacity-40 transition-colors">
                OK
              </button>
              <button onClick={() => setLookupOpen(null)}
                className="px-5 py-1.5 text-xs font-medium bg-white text-[#333] rounded border border-sap-border
                           hover:bg-[#f5f5f5] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Locations - Setup popup ═══ */}
      {locSetupOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30">
          <div ref={locRef} className="bg-[#F4F3EF] border border-[#A09C94] rounded-lg shadow-2xl"
            style={{ width: 380, maxHeight: 440 }}>
            {/* Title bar */}
            <div className="px-4 py-2 bg-gray-50 border-b border-sap-border rounded-t-lg flex items-center justify-between">
              <span className="text-xs font-semibold text-white">Locations - Setup</span>
              <button onClick={() => setLocSetupOpen(false)} className="text-sap-textLight hover:text-sap-text text-sm font-bold px-1">×</button>
            </div>
            {/* Table */}
            <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#E8E4DC] sticky top-0">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-semibold text-[11px] text-[#333] w-10">#</th>
                    <th className="px-3 py-1.5 text-left font-semibold text-[11px] text-[#333]">Name</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {locRows.map((row, idx) => (
                    <tr key={row.location_id || `new-${idx}`}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F8F8F6]'}>
                      <td className="px-3 py-1 text-[11px] text-[#666]">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <input value={row.name}
                          onChange={e => {
                            const copy = [...locRows];
                            copy[idx] = { ...copy[idx], name: e.target.value, _dirty: true };
                            // auto-add blank row at end
                            if (idx === copy.length - 1 && e.target.value.trim()) {
                              copy.push({ location_id: null, name: '', _dirty: false });
                            }
                            setLocRows(copy);
                          }}
                          placeholder={idx === locRows.length - 1 ? 'Add new location…' : ''}
                          className="w-full h-[24px] px-2 text-xs border border-sap-border rounded bg-white
                                     focus:outline-none focus:ring-1 focus:ring-sap-blue/40" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        {/* Don't show delete on the last (empty) row */}
                        {!(idx === locRows.length - 1 && !row.name.trim()) && (
                          <button onClick={() => setLocRows(locRows.filter((_, i) => i !== idx))}
                            className="text-[#999] hover:text-red-600 text-sm font-bold leading-none" title="Remove">×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-[#C4BFB6] bg-[#E8E4DC] rounded-b-lg flex items-center justify-end gap-3">
              <button onClick={saveLocations}
                className="px-5 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded
                           hover:bg-sap-blueDark transition-colors">OK</button>
              <button onClick={() => setLocSetupOpen(false)}
                className="px-5 py-1.5 text-xs font-medium bg-white text-[#333] rounded border border-sap-border
                           hover:bg-[#f5f5f5] transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
